/**
 * Crew alerts: messages from the office to fitters and labourers, shown in their
 * notification bell.
 *
 * notifications.is_read is shared by everyone who can see a row, so an alert is
 * written as one row per recipient (metadata.recipientId). Each person reads and
 * dismisses their own copy, and the office can see how many have read it.
 */
import { directSupabase } from '../lib/supabaseClient';

export const CREW_ALERT_TYPE = 'crew_alert';

export type CrewAlertPriority = 'info' | 'important' | 'urgent';
export type CrewAlertAudience = 'all' | 'fitters' | 'labourers' | 'selected';

export const CREW_ALERT_PRIORITY_LABELS: Record<CrewAlertPriority, string> = {
  info: 'Info',
  important: 'Important',
  urgent: 'Urgent',
};

export const CREW_ALERT_AUDIENCE_LABELS: Record<CrewAlertAudience, string> = {
  all: 'All fitters and labourers',
  fitters: 'Fitters only',
  labourers: 'Labourers only',
  selected: 'Choose people',
};

export const CREW_ALERT_MAX_LENGTH = 500;

export type CrewMember = {
  id: string;
  name: string;
  role: 'fitter' | 'labourer';
};

export type CrewAlertMetadata = {
  alertId: string;
  recipientId: string;
  recipientName: string;
  title: string | null;
  priority: CrewAlertPriority;
  senderId: string | null;
  senderName: string | null;
  audience: CrewAlertAudience;
};

export type CrewAlertSummary = {
  alertId: string;
  createdAt: string;
  title: string | null;
  message: string;
  priority: CrewAlertPriority;
  senderName: string | null;
  recipients: number;
  read: number;
  unreadNames: string[];
};

export function isCrewAlertType(type?: string | null): boolean {
  return String(type || '').trim() === CREW_ALERT_TYPE;
}

export function normaliseCrewAlertPriority(value: unknown): CrewAlertPriority {
  return value === 'important' || value === 'urgent' ? value : 'info';
}

/** A crew alert row is only for the person it was addressed to. */
export function crewAlertVisible(args: { viewerId?: string | null; metadata?: any }): boolean {
  const viewerId = String(args.viewerId || '').trim();
  const recipientId = String(args.metadata?.recipientId || '').trim();
  return !!viewerId && viewerId === recipientId;
}

export function crewAlertLabel(metadata?: any): string {
  const priority = normaliseCrewAlertPriority(metadata?.priority);
  if (priority === 'urgent') return 'Urgent alert';
  if (priority === 'important') return 'Important alert';
  return 'Office alert';
}

/** Who receives the alert. Selected people must still be active crew. */
export function crewAlertRecipients(
  crew: CrewMember[],
  audience: CrewAlertAudience,
  selectedIds: string[] = [],
): CrewMember[] {
  if (audience === 'fitters') return crew.filter((member) => member.role === 'fitter');
  if (audience === 'labourers') return crew.filter((member) => member.role === 'labourer');
  if (audience === 'selected') {
    const chosen = new Set(selectedIds);
    return crew.filter((member) => chosen.has(member.id));
  }
  return crew;
}

/** Returns the first problem with an alert, or null when it can be sent. */
export function validateCrewAlert(args: { message: string; recipients: CrewMember[] }): string | null {
  const message = args.message.trim();
  if (!message) return 'Write the alert message.';
  if (message.length > CREW_ALERT_MAX_LENGTH) return `Keep the message to ${CREW_ALERT_MAX_LENGTH} characters or fewer.`;
  if (args.recipients.length === 0) return 'Choose at least one person to send it to.';
  return null;
}

/** The text shown in the bell: the title, then the message. */
export function crewAlertText(title: string | null | undefined, message: string): string {
  const heading = String(title || '').trim();
  const body = message.trim();
  return heading ? `${heading}: ${body}` : body;
}

/** One notifications row per recipient. */
export function buildCrewAlertRows(args: {
  alertId: string;
  title?: string | null;
  message: string;
  priority: CrewAlertPriority;
  audience: CrewAlertAudience;
  sender: { id: string | null; name: string | null };
  recipients: CrewMember[];
}): Array<{ type: string; message: string; is_read: boolean; metadata: CrewAlertMetadata }> {
  const title = String(args.title || '').trim() || null;
  const text = crewAlertText(title, args.message);
  return args.recipients.map((recipient) => ({
    type: CREW_ALERT_TYPE,
    message: text,
    is_read: false,
    metadata: {
      alertId: args.alertId,
      recipientId: recipient.id,
      recipientName: recipient.name,
      title,
      priority: args.priority,
      senderId: args.sender.id,
      senderName: args.sender.name,
      audience: args.audience,
    },
  }));
}

/** Groups per-recipient rows back into one line per alert, with read counts. Newest first. */
export function summariseCrewAlerts(rows: Array<{ created_at: string; message: string; is_read: boolean; metadata?: any }>): CrewAlertSummary[] {
  const byAlert = new Map<string, CrewAlertSummary>();
  for (const row of rows) {
    const meta = row.metadata || {};
    const alertId = String(meta.alertId || '').trim();
    if (!alertId) continue;
    let summary = byAlert.get(alertId);
    if (!summary) {
      summary = {
        alertId,
        createdAt: row.created_at,
        title: meta.title ?? null,
        message: row.message,
        priority: normaliseCrewAlertPriority(meta.priority),
        senderName: meta.senderName ?? null,
        recipients: 0,
        read: 0,
        unreadNames: [],
      };
      byAlert.set(alertId, summary);
    }
    summary.recipients += 1;
    if (row.is_read) summary.read += 1;
    else if (meta.recipientName) summary.unreadNames.push(String(meta.recipientName));
    if (row.created_at < summary.createdAt) summary.createdAt = row.created_at;
  }
  return Array.from(byAlert.values()).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function newAlertId(): string {
  const cryptoApi = (globalThis as any).crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `alert-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const crewAlertsService = {
  /** Active fitters and labourers. */
  async listCrew(): Promise<CrewMember[]> {
    const { data, error } = await directSupabase
      .from('profiles')
      .select('id, full_name, email, role')
      .eq('group', 'staff')
      .eq('is_active', true)
      .in('role', ['fitter', 'labourer'])
      .order('full_name', { ascending: true });
    if (error) throw new Error(error.message);
    return ((data || []) as Array<{ id: string; full_name?: string | null; email?: string | null; role: string }>).map((row) => ({
      id: row.id,
      name: String(row.full_name || row.email || 'Crew member').trim(),
      role: row.role === 'labourer' ? 'labourer' : 'fitter',
    }));
  },

  async send(args: {
    title?: string | null;
    message: string;
    priority: CrewAlertPriority;
    audience: CrewAlertAudience;
    selectedIds?: string[];
    crew: CrewMember[];
    sender: { id: string | null; name: string | null };
  }): Promise<number> {
    const recipients = crewAlertRecipients(args.crew, args.audience, args.selectedIds);
    const problem = validateCrewAlert({ message: args.message, recipients });
    if (problem) throw new Error(problem);
    const rows = buildCrewAlertRows({
      alertId: newAlertId(),
      title: args.title,
      message: args.message,
      priority: args.priority,
      audience: args.audience,
      sender: args.sender,
      recipients,
    });
    const { error } = await directSupabase.from('notifications').insert(rows);
    if (error) throw new Error(error.message);
    return rows.length;
  },

  /** Recent alerts with read counts, for the office. */
  async listRecent(limit = 400): Promise<CrewAlertSummary[]> {
    const { data, error } = await directSupabase
      .from('notifications')
      .select('created_at, message, is_read, metadata')
      .eq('type', CREW_ALERT_TYPE)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return summariseCrewAlerts((data || []) as any[]).slice(0, 10);
  },
};
